import { FileChip } from '@/components/file-chip';
import { cn } from '@/lib/utils';

export interface MessageFileRef {
  filename: string;
  fileId?: string;
}

const MANIFEST_HEADER = 'Attached files:';
const MANIFEST_LINE = /^- (.+) \(file_id: ([^)\s]+)\)$/;

/**
 * Reverses `formatAgentMessageWithAttachments`: splits the trailing
 * "Attached files:" manifest out of a user message so attachments can be
 * rendered as typed chips instead of flattened text.
 */
export function splitMessageAttachments(content: string): {
  text: string;
  attachments: MessageFileRef[];
} {
  const raw = { text: content, attachments: [] as MessageFileRef[] };
  const idx = content.lastIndexOf(MANIFEST_HEADER);
  if (idx === -1) return raw;
  const before = content.slice(0, idx);
  if (before !== '' && !before.endsWith('\n\n')) return raw;
  const lines = content
    .slice(idx + MANIFEST_HEADER.length)
    .split('\n')
    .filter((line) => line.trim() !== '');
  if (lines.length === 0) return raw;
  const attachments: MessageFileRef[] = [];
  for (const line of lines) {
    const match = MANIFEST_LINE.exec(line);
    if (!match) return raw;
    attachments.push({ filename: match[1], fileId: match[2] });
  }
  return { text: before.trimEnd(), attachments };
}

interface FileChipsProps {
  files: MessageFileRef[];
  className?: string;
}

export function FileChips({ files, className }: FileChipsProps) {
  if (files.length === 0) return null;
  return (
    <div className={cn('flex flex-col items-start gap-2', className)}>
      {files.map((file, i) => (
        <FileChip
          key={file.fileId ?? `${file.filename}-${i}`}
          filename={file.filename}
        />
      ))}
    </div>
  );
}
